import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { formatSar } from '../lib/marketPricing';
import { exclusiveDesc, exclusiveFromSar, exclusiveImages, exclusiveProductRef, exclusiveTitle, fetchExclusive } from '../lib/marketExclusives';
import { buildProductSpecs } from '../lib/productMedia';
import { productPageCopy } from '../lib/productPageCopy';
import { T as DASH_T } from '../lib/supplierDashboardConstants';
import MarketShell from '../components/market/MarketShell';
import ProductGallery from '../components/product/ProductGallery';
import SpecRows from '../components/product/SpecRows';
import { ClampText } from '../components/product/ProductPageParts';
import { EXCL_T } from './MarketExclusives';

// /market/exclusives/:id — one «حصريات معبر» product. The supplier product
// page's own parts (gallery, spec rows, description), in the store's shell.
// One action: «تواصل وتفاوض» opens the chat with the team member, the product
// attached to the first message (Chat.jsx reads it from route state). There
// is no cart here: the price, quantity and shipping are agreed in the chat and
// paid through the invoice sent there.

const D_T = {
  ar: { cta: 'تواصل وتفاوض', ctaNote: 'محادثة مباشرة مع فريق معبر، والترجمة تلقائية. تفاوض على السعر والكمية والشحن، ثم تصلك فاتورة بالريال في المحادثة.', signIn: 'سجّل دخولك لتبدأ المحادثة', missing: 'هذا المنتج غير متاح الآن.', all: 'كل الحصريات', own: 'هذا منتجك — المحادثات تصلك في صفحة الرسائل.', lead: (a, b) => (a && b ? `مدة التجهيز ${a}–${b} يومًا` : a ? `مدة التجهيز ${a} يومًا` : '') },
  en: { cta: 'Talk & negotiate', ctaNote: 'A direct chat with the MAABAR team, auto-translated. Negotiate price, quantity and shipping, then receive a SAR invoice in the chat.', signIn: 'Sign in to start the chat', missing: 'This product is not available right now.', all: 'All exclusives', own: 'This is your product — chats reach you in your messages.', lead: (a, b) => (a && b ? `Lead time ${a}–${b} days` : a ? `Lead time ${a} days` : '') },
  zh: { cta: '沟通洽谈', ctaNote: '与 MAABAR 团队直接聊天，自动翻译。洽谈价格、数量和运费后，您会在聊天中收到沙特里亚尔发票。', signIn: '登录后开始聊天', missing: '该产品暂不可用。', all: '全部独家', own: '这是您的产品——聊天会出现在您的消息中。', lead: (a, b) => (a && b ? `交期 ${a}–${b} 天` : a ? `交期 ${a} 天` : '') },
};

export default function MarketExclusiveDetail({ lang = 'ar', user }) {
  const { id } = useParams();
  const nav = useNavigate();
  const c = { ...(EXCL_T[lang] || EXCL_T.ar), ...(D_T[lang] || D_T.ar) };
  const pc = productPageCopy(lang);
  const dt = DASH_T[lang] || DASH_T.en;
  usePageTitle('market', lang);
  const [p, setP] = useState(undefined);
  useEffect(() => {
    let alive = true;
    setP(undefined);
    fetchExclusive(id).then((row) => { if (alive) setP(row); });
    return () => { alive = false; };
  }, [id]);

  const talk = () => {
    if (!user) { nav(`/login?next=${encodeURIComponent(`/market/exclusives/${id}`)}`); return; }
    nav(`/chat/${p.supplier_id}`, { state: { product: exclusiveProductRef(p) } });
  };

  const title = p ? exclusiveTitle(p, lang) : '';
  const sar = p ? exclusiveFromSar(p) : null;
  const moq = Number(p?.moq) || 0;
  const lead = p ? c.lead(p.lead_time_min_days, p.lead_time_max_days) : '';
  const own = !!(p && user?.id && user.id === p.supplier_id);
  const descr = p ? exclusiveDesc(p, lang) : '';
  return (
    <MarketShell lang={lang} user={user}>
      <div className="mk-wrap" style={{ paddingBottom: 48 }}>
        <div className="mk-crumbs" aria-label="breadcrumb">
          <button type="button" onClick={() => nav('/market')}>{c.market}</button>
          <span aria-hidden="true">›</span>
          <button type="button" onClick={() => nav('/market/exclusives')}>{c.title}</button>
          {p && <><span aria-hidden="true">›</span><span aria-current="page">{title}</span></>}
        </div>
        {p === undefined ? <div className="mk-skel" style={{ height: 420, marginTop: 16 }} />
          : p === null ? (
            <div style={{ marginTop: 24 }}>
              <p className="mk-empty" data-exclusive-missing="1">{c.missing}</p>
              <div style={{ textAlign: 'center' }}><button type="button" className="mk-btn ghost" onClick={() => nav('/market/exclusives')}>{c.all}</button></div>
            </div>
          ) : (
            <div className="mk-excl-detail" data-exclusive-product={p.id}>
              <div className="mk-excl-gallery"><ProductGallery images={exclusiveImages(p)} alt={title} zoomTitle={dt.pdZoomHint} /></div>
              <div className="mk-excl-info">
                <span className="mk-excl-kicker">{c.title}</span>
                <h1>{title}</h1>
                <p className="mk-excl-price" data-excl-price={sar ?? ''}>
                  {sar != null ? <>{c.from} <span className="mk-ltr">{formatSar(sar)}</span></> : c.onRequest}
                </p>
                {(moq > 0 || lead) && <p className="mk-moq">{[moq > 0 ? `${c.moq}: ${moq.toLocaleString('en-US')}` : '', lead].filter(Boolean).join(' · ')}</p>}
                <div className="mk-excl-cta">
                  {own ? <p className="mk-note" data-exclusive-own="1">{c.own}</p> : (
                    <>
                      <button type="button" className="mk-btn" data-exclusive-talk="1" onClick={talk}>{user ? c.cta : c.signIn}</button>
                      <p className="mk-note">{c.ctaNote}</p>
                    </>
                  )}
                </div>
                <SpecRows
                  lang={lang}
                  emptyMode="omit"
                  rows={[
                    ...buildProductSpecs(p, lang).map((s) => ({ key: s.key, label: s.label, value: s.value })),
                    ...(p.sample_available ? [{ key: 'samples', label: pc.samples, value: pc.samplesAvailable }] : []),
                    ...(descr ? [{ key: 'desc', label: dt.pdDescLabel, children: <ClampText lang={lang} text={descr} style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.7 }} /> }] : []),
                  ]}
                />
              </div>
            </div>
          )}
      </div>
    </MarketShell>
  );
}
